import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Copy, X } from "lucide-react";
import { xeroApi } from "../../backend/api/xero";
import type { SyncRun, SyncRunLine } from "../../backend/api/xero";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  LevelBadge,
  StatusBadge,
  Stat,
  formatDateTime,
  formatDuration,
  formatTime,
  triggeredBy,
} from "./shared";

/** One run's log, readable or raw. */
export default function RunDetail() {
  const { integrationId = "", runId = "" } = useParams();

  const [run, setRun] = useState<SyncRun | null>(null);
  const [lines, setLines] = useState<SyncRunLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    const { data, error: loadError } = await xeroApi.getRun(runId);
    if (!quiet) setLoading(false);

    if (loadError) {
      setError(loadError.message || "Could not load this run.");
      return;
    }

    setError("");
    setRun(data?.run || null);
    setLines(data?.lines || []);
  }, [runId]);

  useEffect(() => {
    load();
  }, [load]);

  // A run in progress keeps writing to its log, so the page tails it. This is
  // what makes "open the log while it is running" show something live rather
  // than a snapshot taken before the work started.
  const live = run?.status === "queued" || run?.status === "running";

  useEffect(() => {
    if (!live) return undefined;
    const timer = window.setInterval(() => load(true), 2000);
    return () => window.clearInterval(timer);
  }, [live, load]);

  const cancel = async () => {
    const { error: cancelError } = await xeroApi.cancelRun(runId);
    if (cancelError) {
      setError(cancelError.message || "Could not cancel this run.");
    }
    await load(true);
  };

  // The raw view shows the same records the API returned, so the readable view
  // can never quietly disagree with what was stored.
  const raw = useMemo(() => JSON.stringify({ run, lines }, null, 2), [run, lines]);

  const copy = async () => {
    await navigator.clipboard.writeText(raw);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <main className="mx-auto flex max-w-[1500px] flex-col gap-5 p-4 sm:p-6">
      <Link
        to={`/integrations/xero/syncs/${integrationId}`}
        className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Run history
      </Link>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Run log</h1>
          <p className="text-sm text-muted-foreground">
            {run ? formatDateTime(run.started_at) : "Loading..."}
            {live && " - updating live"}
          </p>
        </div>
        {live && (
          <Button
            variant="outline"
            className="text-destructive hover:text-destructive"
            disabled={Boolean(run?.cancel_requested_at)}
            onClick={cancel}
          >
            <X className="h-4 w-4" />
            {run?.cancel_requested_at ? "Cancelling..." : "Cancel run"}
          </Button>
        )}
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTitle>Could not load this run</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {run && (
        <Card>
          <CardContent className="pt-6">
            <dl className="grid gap-4 sm:grid-cols-3 lg:grid-cols-5">
              <Stat label="Status">
                <StatusBadge status={run.status} />
              </Stat>
              <Stat label="Duration">{formatDuration(run.duration_ms)}</Stat>
              <Stat label="Triggered by">{triggeredBy(run)}</Stat>
              <Stat label="Organisation">{run.tenant_name || "-"}</Stat>
              <Stat label="Result">
                {run.created_count} added, {run.updated_count} updated,{" "}
                {run.unchanged_count} unchanged
                {run.skipped_count > 0 ? `, ${run.skipped_count} skipped` : ""}
              </Stat>
            </dl>
            {run.error && (
              <p className="mt-4 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {run.error}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <CardTitle className="text-base">Log</CardTitle>
            <CardDescription>
              {loading ? "Loading..." : `${lines.length} ${lines.length === 1 ? "line" : "lines"}`}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="formatted">
            <div className="mb-3 flex items-center justify-between gap-3">
              <TabsList>
                <TabsTrigger value="formatted">Formatted</TabsTrigger>
                <TabsTrigger value="json">JSON</TabsTrigger>
              </TabsList>
              <Button variant="outline" size="sm" onClick={copy}>
                <Copy className="h-4 w-4" />
                {copied ? "Copied" : "Copy JSON"}
              </Button>
            </div>

            <TabsContent value="formatted">
              <div className="max-h-[55vh] overflow-auto rounded-md border bg-muted/40 p-3">
                {lines.length === 0 ? (
                  <p className="p-6 text-center text-sm text-muted-foreground">
                    No log lines recorded for this run.
                  </p>
                ) : (
                  <ul className="space-y-1.5 text-sm">
                    {lines.map((line) => (
                      <li key={line.id} className="flex flex-wrap items-baseline gap-2">
                        <span className="font-mono text-xs text-muted-foreground">
                          {formatTime(line.logged_at)}
                        </span>
                        <LevelBadge level={line.level} />
                        <span className="break-all">{line.message}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </TabsContent>

            <TabsContent value="json">
              <pre className="max-h-[55vh] overflow-auto rounded-md border bg-muted/40 p-3 font-mono text-xs">
                {raw}
              </pre>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </main>
  );
}
